import type CartAddedItemInfo from "@/interfaces/CartAddedItemInfo";

// Obtener el ID de Google Analytics y Google Tag Manager
const googleAnalyticsId = import.meta.env.VITE_GOOGLE_ANALYTICS_ID;
const googleTagManagerId = import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID;
const googleAnalyticsEnabled = import.meta.env.VITE_GOOGLE_ANALYTICS_ENABLED;

export interface DataLayerItem {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_category?: string;
  promotion_name?: string;
  price: number;
  quantity: number;
  discount?: number;
}

export interface DataLayerEcommerce {
  currency?: string;
  value?: number;
  payment_type?: string;
  coupon?: string;
  items?: DataLayerItem[];
  method?: string;
  shipping?: number;
  search_term?: string;
  virtual_currency_name?: string;
  item_name?: string;
}

export interface DataLayerEvent {
  ecommerce: DataLayerEcommerce | null;
  event?: string;
}

interface CartAnalyticsItem {
  product: {
    id: string | number;
    part_number?: string | null;
    name: string;
    brand?: { name?: string | null } | null;
    category?: { name?: string | null } | null;
    promotion?: { name?: string | null } | null;
    price_detail?: {
      common_quantity?: number;
      common_price_with_tax: number;
      promotion_quantity?: number;
      promotion_price_with_tax: number;
    } | null;
  };
}

export class GoogleAnalyticsService {
  public async init() {
    if (!googleAnalyticsEnabled) {
      return;
    }
    // Agregar el script de Google Analytics
    const gaScript = document.createElement("script");
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
    const gaInlineScript = document.createElement("script");
    gaInlineScript.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${googleAnalyticsId}');
    `;
    document.head.prepend(gaInlineScript);
    document.head.prepend(gaScript);

    // Agregar el script de Google Tag Manager
    const gtmScript = document.createElement("script");
    gtmScript.textContent = `
      (function(w,d,s,l,i){
        w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
        var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
        j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
        f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${googleTagManagerId}');
    `;
    document.head.appendChild(gtmScript);

    // Agregar el noscript de Google Tag Manager
    const gtmNoscript = document.createElement("noscript");
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${googleTagManagerId}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    gtmNoscript.appendChild(iframe);
    document.body.appendChild(gtmNoscript);

    return new Promise((resolve, reject) => {
      gaScript.onload = () => resolve(null);
      gaScript.onerror = () => reject();
    });
  }

  private initialized = false;
  public async initOnce() {
    if (this.initialized || !googleAnalyticsEnabled) {
      return;
    }
    this.initialized = true;
    await this.init();
  }

  public getDataLayer(): DataLayerEvent[] {
    const windowFixed = window as unknown as { dataLayer: DataLayerEvent[] };
    const dataLayer = windowFixed.dataLayer;
    return dataLayer;
  }

  public async pushEvent() {
    if (!googleAnalyticsEnabled) {
      return;
    }
    await this.initOnce();
    const windowFixed = window as unknown as { dataLayer: DataLayerEvent[] };
    const dataLayer = windowFixed.dataLayer;
    if (!dataLayer) {
      throw "No se pudo agregar el evento analytics porque no se ha cargado el objeto window.dataLayer para Google Analytics.";
    }
    windowFixed.dataLayer.push({ ecommerce: null });
  }

  public async addToCartEvent(info: CartAddedItemInfo) {
    if (!googleAnalyticsEnabled) {
      return;
    }
    await this.initOnce();
    const dlItems: DataLayerItem[] = [];
    if (info.common_quantity != 0) {
      dlItems.push({
        item_id: info.part_number || "DIG-" + info.product_id,
        item_name: info.product_name,
        item_brand: info.brand_name,
        item_category: info.category_name,
        price: info.common_price_with_tax,
        quantity: Math.abs(info.common_quantity),
      });
    }
    if (info.promotion_quantity != 0) {
      dlItems.push({
        item_id: info.part_number || "DIG-" + info.product_id,
        item_name: info.product_name,
        item_brand: info.brand_name,
        item_category: info.category_name,
        promotion_name: info.promotion_name,
        price: info.promotion_price_with_tax,
        quantity: Math.abs(info.promotion_quantity),
        discount:
          (info.common_price_with_tax - info.promotion_price_with_tax) *
          info.promotion_quantity,
      });
    }
    this.getDataLayer().push({ ecommerce: null });
    const event: DataLayerEvent = {
      event: info.total_quantity > 0 ? "add_to_cart" : "remove_from_cart",
      ecommerce: {
        currency: "MXN",
        value: info.total_with_tax,
        items: dlItems,
      },
    };
    this.getDataLayer().push(event);
    console.log(event);
  }

  public async addPaymentInfo(
    total: number,
    dlItems: DataLayerItem[],
    paymentType: string,
    couponCode?: string,
  ) {
    if (!googleAnalyticsEnabled) {
      return;
    }
    await this.initOnce();
    this.getDataLayer().push({ ecommerce: null });
    const ecommerce = {
      currency: "MXN",
      value: total,
      payment_type: paymentType,
      items: dlItems,
    } as DataLayerEcommerce;
    if (couponCode) {
      ecommerce.coupon = couponCode;
    }
    const event: DataLayerEvent = {
      event: "add_payment_info",
      ecommerce,
    };
    this.getDataLayer().push(event);
    console.log(event);
  }

  public async viewCart(total: number, dlItems: DataLayerItem[]) {
    if (!googleAnalyticsEnabled) {
      return;
    }
    await this.initOnce();
    this.getDataLayer().push({ ecommerce: null });
    const ecommerce = {
      currency: "MXN",
      value: parseFloat(total.toFixed(2)),
      items: dlItems,
    } as DataLayerEcommerce;
    const event: DataLayerEvent = {
      event: "view_cart",
      ecommerce,
    };
    this.getDataLayer().push(event);
    console.log(event);
  }

  public async beginCheckout(
    total: number,
    dlItems: DataLayerItem[],
    couponCode?: string,
  ) {
    if (!googleAnalyticsEnabled) {
      return;
    }
    await this.initOnce();
    this.getDataLayer().push({ ecommerce: null });
    const ecommerce = {
      currency: "MXN",
      value: parseFloat(total.toFixed(2)),
      items: dlItems,
    } as DataLayerEcommerce;
    if (couponCode) {
      ecommerce.coupon = couponCode;
    }
    const event: DataLayerEvent = {
      event: "begin_checkout",
      ecommerce,
    };
    this.getDataLayer().push(event);
    console.log(event);
  }

  public async login(method: string = "email") {
    if (!googleAnalyticsEnabled) {
      return;
    }
    await this.initOnce();
    const event: DataLayerEvent = {
      event: "login",
      ecommerce: {
        method,
      },
    };
    this.getDataLayer().push(event);
    console.log(event);
  }

  public async purchase(
    total: number,
    transactionId: string,
    dlItems: DataLayerItem[],
    couponCode?: string,
    shipping?: number,
  ) {
    if (!googleAnalyticsEnabled) {
      return;
    }
    await this.initOnce();
    this.getDataLayer().push({ ecommerce: null });
    const ecommerce = {
      currency: "MXN",
      transaction_id: transactionId,
      value: parseFloat(total.toFixed(2)),
      items: dlItems,
    } as DataLayerEcommerce;
    if (couponCode) {
      ecommerce.coupon = couponCode;
    }
    if (shipping) {
      ecommerce.shipping = shipping;
    }
    const event: DataLayerEvent = {
      event: "purchase",
      ecommerce,
    };
    this.getDataLayer().push(event);
    console.log(event);
  }

  public async search(term: string) {
    if (!googleAnalyticsEnabled) {
      return;
    }
    await this.initOnce();
    const event: DataLayerEvent = {
      event: "search",
      ecommerce: {
        search_term: term,
      },
    };
    this.getDataLayer().push(event);
    console.log(event);
  }

  public async signUp(method: string = "email") {
    if (!googleAnalyticsEnabled) {
      return;
    }
    await this.initOnce();
    const event: DataLayerEvent = {
      event: "sign_up",
      ecommerce: {
        method,
      },
    };
    this.getDataLayer().push(event);
    console.log(event);
  }

  public async spendVirtualCurrency(
    value: number,
    dlItems: DataLayerItem[],
    currencyName: string = "monedero",
  ) {
    if (!googleAnalyticsEnabled) {
      return;
    }
    await this.initOnce();
    const event: DataLayerEvent = {
      event: "spend_virtual_currency",
      ecommerce: {
        virtual_currency_name: currencyName,
        item_name: dlItems.map((item) => item.item_name).join(" | "),
        value,
      },
    };
    this.getDataLayer().push(event);
    console.log(event);
  }

  public async earnVirtualCurrency(
    value: number,
    dlItems: DataLayerItem[],
    currencyName: string = "monedero",
  ) {
    if (!googleAnalyticsEnabled) {
      return;
    }
    await this.initOnce();
    const event: DataLayerEvent = {
      event: "earn_virtual_currency",
      ecommerce: {
        virtual_currency_name: currencyName,
        item_name: dlItems.map((item) => item.item_name).join(" | "),
        value,
      },
    };
    this.getDataLayer().push(event);
    console.log(event);
  }

  public formatFromCartItems(items: CartAnalyticsItem[]) {
    const dlItems: DataLayerItem[] = [];
    items.forEach((item) => {
      const product = item.product;
      const priceDetail = product?.price_detail;

      if (!product || !priceDetail) {
        return;
      }

      if (priceDetail.common_quantity != 0) {
        dlItems.push({
          item_id: product.part_number || "DIG-" + product.id,
          item_name: product.name,
          item_brand: product.brand?.name,
          item_category: product.category?.name,
          price: priceDetail.common_price_with_tax,
          quantity: Math.abs(priceDetail.common_quantity || 0),
        });
      }
      if (priceDetail.promotion_quantity != 0) {
        dlItems.push({
          item_id: product.part_number || "DIG-" + product.id,
          item_name: product.name,
          item_brand: product.brand?.name,
          item_category: product.category?.name,
          promotion_name: product.promotion?.name,
          price: priceDetail.promotion_price_with_tax,
          quantity: Math.abs(priceDetail.promotion_quantity || 0),
          discount:
            (priceDetail.common_price_with_tax -
              priceDetail.promotion_price_with_tax) *
            (priceDetail.promotion_quantity || 0),
        });
      }
    });
    return dlItems;
  }
}

const googleAnalytics = new GoogleAnalyticsService();

export default googleAnalytics;
